/**
 * Log Viewer Module
 * Handles filtering, searching, and display of log entries.
 */

( function( $ ) {
	'use strict';

	const LogViewer = {
		/**
		 * Initialise the log viewer.
		 */
		init() {
			const $logViewer = $( '.qala-blocket-connector__log-viewer' );
			if ( ! $logViewer.length ) {
				return;
			}

			this.$logViewer = $logViewer;
			this.$logContent = $logViewer.find( '#qala-log-content' );
			this.$linesLimit = $logViewer.find( '#qala-log-lines-limit' );
			this.$search = $logViewer.find( '#qala-log-search' );
			this.$levelFilter = $logViewer.find( '#qala-log-level-filter' );
			this.$stats = $logViewer.find( '#qala-log-stats' );
			this.$refresh = $logViewer.find( '#qala-log-refresh' );

			// Get full content from data attribute.
			const logData = $logViewer.attr( 'data-log-content' );
			if ( logData ) {
				try {
					this.fullContent = atob( logData );
				} catch ( e ) {
					this.fullContent = this.$logContent.text();
				}
			} else {
				this.fullContent = this.$logContent.text();
			}

			this.totalLines = parseInt( $logViewer.attr( 'data-total-lines' ) ) || 0;

			// Bind events.
			this.bindEvents();

			// Initial filter.
			this.filterLogs();
		},

		/**
		 * Bind event handlers.
		 */
		bindEvents() {
			this.$linesLimit.on( 'change', () => this.filterLogs() );
			this.$search.on( 'input', () => this.filterLogs() );
			this.$levelFilter.on( 'change', () => this.filterLogs() );
			this.$refresh.on( 'click', () => {
				window.location.reload();
			} );
		},

		/**
		 * Filter and display logs based on current filters.
		 */
		filterLogs() {
			const searchTerm = this.$search.val().toLowerCase();
			const levelFilter = this.$levelFilter.val();
			const linesLimit = parseInt( this.$linesLimit.val() ) || 500;

			const allLines = this.fullContent.split( '\n' );
			let filteredLines = allLines;

			// Filter by level.
			if ( levelFilter ) {
				filteredLines = filteredLines.filter( line => {
					return line.indexOf( '[' + levelFilter + ']' ) !== -1;
				} );
			}

			// Filter by search term.
			if ( searchTerm ) {
				filteredLines = filteredLines.filter( line => {
					return line.toLowerCase().indexOf( searchTerm ) !== -1;
				} );
			}

			// Limit lines (take last N for newest first).
			let displayLines = filteredLines;
			if ( linesLimit > 0 && filteredLines.length > linesLimit ) {
				displayLines = filteredLines.slice( -linesLimit );
			}

			// Apply color coding and display.
			const html = this.formatLogLines( displayLines, searchTerm );
			this.$logContent.html( html );

			// Update stats.
			const showing = displayLines.length;
			const total = filteredLines.length;
			let statsText = `Showing ${ showing } of ${ total } lines`;
			if ( total < allLines.length ) {
				statsText += ` (filtered from ${ allLines.length } total)`;
			}
			this.$stats.text( statsText );
		},

		/**
		 * Format log lines with color coding and search highlighting.
		 *
		 * @param {Array}  lines      - Array of log lines.
		 * @param {string} searchTerm - Search term to highlight.
		 * @return {string} HTML string.
		 */
		formatLogLines( lines, searchTerm ) {
			let html = '';

			lines.forEach( line => {
				if ( ! line.trim() ) {
					html += '\n';
					return;
				}

				const levelInfo = this.getLevelInfo( line );
				let formattedLine = line;

				// Highlight search term.
				if ( searchTerm ) {
					formattedLine = this.highlightSearchTerm( formattedLine, searchTerm );
				}

				html += `<div class="qala-log-line qala-log-${ levelInfo.class }" style="color: ${ levelInfo.color }; background: ${ levelInfo.bgColor }; padding: 2px 0;">${ formattedLine }</div>`;
			} );

			return html;
		},

		/**
		 * Get level information for a log line.
		 *
		 * @param {string} line - Log line.
		 * @return {Object} Level info with class, color, and bgColor.
		 */
		getLevelInfo( line ) {
			if ( line.indexOf( '[DEBUG]' ) !== -1 ) {
				return {
					class: 'debug',
					color: '#9cdcfe',
					bgColor: 'transparent',
				};
			} else if ( line.indexOf( '[INFO]' ) !== -1 ) {
				return {
					class: 'info',
					color: '#4ec9b0',
					bgColor: 'transparent',
				};
			} else if ( line.indexOf( '[NOTICE]' ) !== -1 ) {
				return {
					class: 'notice',
					color: '#dcdcaa',
					bgColor: 'transparent',
				};
			} else if ( line.indexOf( '[WARNING]' ) !== -1 ) {
				return {
					class: 'warning',
					color: '#ce9178',
					bgColor: 'rgba(255, 193, 7, 0.1)',
				};
			} else if ( line.indexOf( '[ERROR]' ) !== -1 ) {
				return {
					class: 'error',
					color: '#f48771',
					bgColor: 'rgba(220, 53, 69, 0.1)',
				};
			} else if ( line.indexOf( '[CRITICAL]' ) !== -1 ) {
				return {
					class: 'critical',
					color: '#ff6b6b',
					bgColor: 'rgba(220, 53, 69, 0.2)',
				};
			}

			return {
				class: 'default',
				color: '#d4d4d4',
				bgColor: 'transparent',
			};
		},

		/**
		 * Highlight search term in a line.
		 *
		 * @param {string} line       - Log line.
		 * @param {string} searchTerm - Search term to highlight.
		 * @return {string} Line with highlighted search term.
		 */
		highlightSearchTerm( line, searchTerm ) {
			const escapedTerm = this.escapeRegex( searchTerm );
			const regex = new RegExp( '(' + escapedTerm + ')', 'gi' );
			return line.replace(
				regex,
				'<mark class="qala-log-highlight">$1</mark>'
			);
		},

		/**
		 * Escape special regex characters.
		 *
		 * @param {string} str - String to escape.
		 * @return {string} Escaped string.
		 */
		escapeRegex( str ) {
			let escaped = '';
			for ( let i = 0; i < str.length; i++ ) {
				const char = str[ i ];
				if (
					char === '.' ||
					char === '*' ||
					char === '+' ||
					char === '?' ||
					char === '^' ||
					char === '$' ||
					char === '{' ||
					char === '}' ||
					char === '(' ||
					char === ')' ||
					char === '|' ||
					char === '[' ||
					char === ']' ||
					char === '\\'
				) {
					escaped += '\\' + char;
				} else {
					escaped += char;
				}
			}
			return escaped;
		},
	};

	// Initialise on document ready.
	$( document ).ready( function() {
		LogViewer.init();
	} );
}( jQuery ) );

